import type {
  EnumPaymentType,
  CashierTransactionViewDetails,
} from "@/api/cashier";
import { useTranslation } from "react-i18next";
import styles from "./TransactionTimeline.module.css";
import {
  TransactionTimelineStep,
  type PendingConfirmationStatus,
} from "./TransactionTimelineStep";
import { TransactionTimelineLoadingSkeleton } from "./TransactionTimelineLoadingSkeleton";

/* The TransactionTimeline component
  This component displays a timeline of transaction steps for a given transaction.
  @param transactionTimeline - An array of transaction timeline steps to display.
  @param transactionType - The type of transaction (e.g., withdrawal, deposit, transfer, cashAdjustment).
  @param isLoading - (Optional) Whether the timeline data is still loading.
  @param onCompleteNow - (Optional) Callback invoked when the customer clicks "Complete now".
  @param pendingConfirmationStatus - (Optional) Pending confirmation data from WithdrawalRequestStatus.
  @returns A ReactElement representing the transaction timeline.
*/
export const TransactionTimeline = ({
  transactionTimeline,
  transactionType,
  isLoading = false,
  onCompleteNow,
  pendingConfirmationStatus,
}: {
  transactionTimeline: CashierTransactionViewDetails["timeline"];
  transactionType: EnumPaymentType;
  isLoading?: boolean;
  onCompleteNow?: () => void;
  pendingConfirmationStatus?: PendingConfirmationStatus;
}) => {
  const { t } = useTranslation();
  return (
    <div className={styles.transactionTimelineContainer}>
      {isLoading ? (
        <TransactionTimelineLoadingSkeleton />
      ) : (
        <>
          <h2 className={styles.title}>
            {t("transactions.history.timeline.title")}
          </h2>
          <div className={styles.timelineSteps}>
            {transactionTimeline && transactionType
              ? transactionTimeline.map((step, index) => (
                  <TransactionTimelineStep
                    step={step}
                    key={index}
                    transactionType={transactionType}
                    onCompleteNow={onCompleteNow}
                    pendingConfirmationStatus={pendingConfirmationStatus}
                  />
                ))
              : null}
          </div>
        </>
      )}
    </div>
  );
};
